import { useState } from "react";

import { TooltipBase } from "@frontend/common";
import { splitMultipleStringList } from "@frontend/common/src/business/DatabaseTable/utils/splitMultipleStringList";

import ListUnsuitableContainer from "@/components/shared/business/ListUnsuitableContainer/ListUnsuitableContainer";
import { UNSUITABLE_REASON_KEYS } from "@/components/shared/business/UnsuitableReason/constants/unsuitableReason.constants";
import { useRegulatoryListFilterQuery } from "@api/regulatory/regulatoryList/useRegulatoryListFilter";
import { DRAFT_STATUS_CODE } from "@constants/draftStatusCode.constants";
import { formatDate } from "@utils/formatDate";
import { handleRegulatoryListError } from "@utils/handleDraftError";

import RegulatoryCompanyShell from "./components/RegulatoryCompanyShell/RegulatoryCompanyShell";
import RegulatoryListStatusContainer from "./components/RegulatoryStatusContainer/RegulatoryStatusContainer";
import RegulatoryTitleContainer from "./components/RegulatoryTitleContainer/RegulatoryTitleContainer";
import * as S from "./RegulatoryTableBody.style";
import { EMPTY_SHELL_VALUE } from "../../constants/RegulatoryTable.constants";

const getMultipleStringItems = (value?: string) =>
  value ? splitMultipleStringList(value).filter(Boolean) : [];

const RegulatoryTableBody = () => {
  const [activeModalId, setActiveModalId] = useState<number | null>(null);

  const { data: regulatoryListData, refetch: updateRegulatoryList } =
    useRegulatoryListFilterQuery();

  const renderLabelIdBulletList = (labelIdList: string[]) => {
    return (
      <S.RegulatoryListBulletList>
        {labelIdList.map((item) => (
          <S.RegulatoryListBulletItem key={`${item}-${labelIdList.join("__")}`}>
            <S.RegulatoryListBulletItemText>
              {item}
            </S.RegulatoryListBulletItemText>
          </S.RegulatoryListBulletItem>
        ))}
      </S.RegulatoryListBulletList>
    );
  };

  const renderLabelId = (labelId?: string, labelIdList: string[] = []) => {
    if (!labelId) {
      return EMPTY_SHELL_VALUE;
    }

    if (!labelId.includes("__")) {
      return labelId;
    }

    if (labelIdList.length === 0) {
      return EMPTY_SHELL_VALUE;
    }

    const visibleLabelIdList = labelIdList.slice(0, 2);
    const remainingCount = labelIdList.length > 2 ? labelIdList.length - 2 : 0;

    return (
      <>
        {renderLabelIdBulletList(visibleLabelIdList)}

        {remainingCount > 0 && (
          <S.RegulatoryListNumber>+{remainingCount}</S.RegulatoryListNumber>
        )}

        {remainingCount > 0 && (
          <S.RegulatoryListTooltipWrapper className="tooltip-wrapper">
            <TooltipBase className="tooltip">
              {renderLabelIdBulletList(labelIdList)}
            </TooltipBase>
          </S.RegulatoryListTooltipWrapper>
        )}
      </>
    );
  };

  return (
    <S.RegulatoryListTableBody>
      {regulatoryListData?.data.map((listItem) => {
        const labelIdList = getMultipleStringItems(listItem?.labelId);
        const hasLabelIdTooltip =
          !!listItem?.labelId?.includes("__") && labelIdList.length > 2;

        return (
          <S.RegulatoryListTableTr
            key={`row-${listItem.id}`}
            isError={handleRegulatoryListError(listItem)[0]}
          >
            <S.RegulatoryListTableId>
              {listItem?.id || EMPTY_SHELL_VALUE}
            </S.RegulatoryListTableId>
            <S.RegulatoryListTablePressId $hasTooltip={hasLabelIdTooltip}>
              {renderLabelId(listItem?.labelId, labelIdList)}
            </S.RegulatoryListTablePressId>
            <S.RegulatoryListTableRegistryAgency>
              {listItem?.regulatoryAgency || EMPTY_SHELL_VALUE}
            </S.RegulatoryListTableRegistryAgency>
            <S.RegulatoryListTableLabelDate>
              {listItem?.labelDate || EMPTY_SHELL_VALUE}
            </S.RegulatoryListTableLabelDate>
            <S.RegulatoryListTableCompany>
              <RegulatoryCompanyShell
                companyName={listItem?.companyList || []}
                regulatoryData={listItem}
                updateRegulatoryList={updateRegulatoryList}
              />
            </S.RegulatoryListTableCompany>
            <S.RegulatoryListTableUnsuitable
              isUnsuitable={
                listItem.draftArticleStatusHighest ===
                DRAFT_STATUS_CODE.unsuitable
              }
              isModalOpen={activeModalId === listItem.draftArticleId}
            >
              <ListUnsuitableContainer
                onModalStateChange={(isOpen) => {
                  if (isOpen) {
                    setActiveModalId(listItem?.draftArticleId || null);
                  } else if (activeModalId === listItem.draftArticleId) {
                    setActiveModalId(null);
                  }
                }}
                id={Number(listItem.draftArticleId)}
                articleRejectReason={listItem?.articleRejectReason || ""}
                duplArticleId={null}
                draftArticleStatusHighest={listItem.draftArticleStatusHighest}
                mcEditorName={listItem.mcEditorName}
                aiDone={listItem.aiDone ?? false}
                unsuitableReasonUsageType={
                  UNSUITABLE_REASON_KEYS.regulatoryList
                }
              />
            </S.RegulatoryListTableUnsuitable>
            <RegulatoryTitleContainer
              regulatoryListItem={listItem}
              updateRegulatoryList={updateRegulatoryList}
            />
            <S.RegulatoryListTableClassification
              status={listItem.draftArticleStatusHighest}
            >
              {listItem?.classification || EMPTY_SHELL_VALUE}
            </S.RegulatoryListTableClassification>
            <S.RegulatoryListTableStatus
              key={listItem.id}
              code={listItem.draftArticleStatusHighest}
            >
              <RegulatoryListStatusContainer data={listItem} />
            </S.RegulatoryListTableStatus>
            <S.RegulatoryListTableDate>
              {listItem.startDate
                ? formatDate(listItem.startDate, "YY.MM.DD HH:mm:ss")
                : EMPTY_SHELL_VALUE}
            </S.RegulatoryListTableDate>
            <S.RegulatoryListTableDate>
              {listItem.completeDate
                ? formatDate(listItem.completeDate, "YY.MM.DD HH:mm:ss")
                : EMPTY_SHELL_VALUE}
            </S.RegulatoryListTableDate>
            <S.RegulatoryListTableMcEditor>
              {listItem.mcEditorName || EMPTY_SHELL_VALUE}
            </S.RegulatoryListTableMcEditor>
            <S.RegulatoryListTableFtEditor>
              {listItem.ftEditor2Name || EMPTY_SHELL_VALUE}
            </S.RegulatoryListTableFtEditor>
          </S.RegulatoryListTableTr>
        );
      })}
    </S.RegulatoryListTableBody>
  );
};

export default RegulatoryTableBody;
